import React from 'react'
import ReactDOM from 'react-dom'
import FocusLock from 'react-focus-lock'
import styled, { getFontSize, MQ_TABLET } from '../../constants/theme'
import { CircleCloseIcon } from '../elements/Icons'

export enum ModalStyle {
  DARK = 'dark',
  LIGHT = 'light',
}

interface Props {
  fitToWindow?: boolean
  isOpen: boolean
  onRequestClose?: (e?: KeyboardEvent | MouseEvent | boolean) => void
  style?: ModalStyle
}

interface State {
  fitWidth: string
}

export const getModalContainer = () => {
  const CONTAINER_ID = 'ModalContainer'
  if (!document.getElementById(CONTAINER_ID)) {
    const div = document.createElement('div')
    div.setAttribute('id', CONTAINER_ID)
    document.body.appendChild(div)
  }
  return document.getElementById(CONTAINER_ID)
}

class Modal extends React.Component<Props, State> {
  static defaultProps = {
    fitToWindow: false,
    style: ModalStyle.DARK,
  }

  containerEl: HTMLElement | null
  contentEl: HTMLElement
  modalEl: HTMLElement

  constructor(props) {
    super(props)
    this.containerEl = getModalContainer()
    this.state = { fitWidth: 'auto' }
    if (props.isOpen) {
      document.body.classList.add('modal-open')
    }
    window.addEventListener('keydown', this.onKeyDown)
    if (props.fitToWindow) {
      window.addEventListener('resize', this.onResize)
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isOpen !== this.props.isOpen) {
      this.setState({ fitWidth: 'auto' })
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      nextState.fitWidth !== this.state.fitWidth ||
      nextProps.isOpen !== this.props.isOpen
    )
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('resize', this.onResize)
    document.body.classList.remove('modal-open')
  }

  onClickCloseModal = e => {
    if (e.target.classList.contains('CloseModal')) {
      this.closeModal(e)
    }
  }

  onKeyDown = e => {
    if (
      (e.key && e.key.toLowerCase() === 'escape') ||
      e.keyCode === 27 ||
      e.which === 27
    ) {
      this.closeModal(e)
    }
  }

  onResize = () => {
    this.setState({ fitWidth: 'auto' }, () => {
      this.calculateFitWidth()
    })
  }

  closeModal = e => {
    e.stopPropagation()
    const { onRequestClose } = this.props
    if (onRequestClose) {
      onRequestClose(e)
    }
  }

  getInitialWidth = comp => {
    if (comp) {
      this.contentEl = comp
      const img = this.contentEl.querySelector('img')
      if (img) {
        img.onload = () => {
          this.calculateFitWidth()
        }
      } else {
        this.calculateFitWidth()
      }
    }
  }

  calculateFitWidth = () => {
    if (this.contentEl) {
      const cw = this.contentEl.scrollWidth
      const ch = this.contentEl.scrollHeight
      const cr = cw / ch // content ratio
      const aw = this.contentEl.offsetWidth
      const ah = this.contentEl.offsetHeight
      const ar = aw / ah // available ratio
      const { value } = getFontSize()
      const newWidth = ar > cr ? cw * (ah / ch) : aw * (aw / cw)
      const increase = newWidth * (value * 0.1875)
      this.setState({
        fitWidth: `${Math.ceil(newWidth - increase)}px`,
      })
    }
  }

  fadeIn = comp => {
    if (comp) {
      this.modalEl = comp
      requestAnimationFrame(() => {
        comp.style.opacity = 1
      })
    }
  }

  render() {
    if (!this.props.isOpen || !this.containerEl) {
      document.body.classList.remove('modal-open')
      return null
    }
    document.body.classList.add('modal-open')
    const isLightStyle = this.props.style === ModalStyle.LIGHT
    return ReactDOM.createPortal(
      <FocusLock returnFocus>
        <ModalOverlay
          className={`CloseModal ${this.props.style}`}
          ref={this.fadeIn}
          key="modal"
          onClick={this.onClickCloseModal}
          role="dialog"
        >
          <ModalContents
            fitWidth={this.state.fitWidth}
            fitToWindow={this.props.fitToWindow}
            ref={this.getInitialWidth}
          >
            {this.props.children}
            <ModalCloseButton
              className="CloseModal"
              isLightStyle={isLightStyle}
              onClick={this.closeModal}
              title="Close modal"
            >
              <CircleCloseIcon />
            </ModalCloseButton>
          </ModalContents>
        </ModalOverlay>
      </FocusLock>,
      this.containerEl,
    )
  }
}

export const ModalOverlay = styled.div(
  {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    bottom: 0,
    display: 'flex',
    left: 0,
    opacity: 0,
    overflowY: 'scroll',
    position: 'fixed',
    right: 0,
    top: 0,
    transition: 'opacity .333s ease',
    zIndex: 3000,
  },
  ({ theme }) => ({
    ...MQ_TABLET({
      padding: theme.padding,
    }),
    padding: theme.paddingMobile,
  }),
)

interface ContentProps {
  fitWidth: string
  fitToWindow?: boolean
}

const ModalContents = styled.div<ContentProps>(
  {
    margin: 'auto',
    maxWidth: '100%',
    overflow: (window as any).CMD.browser.ie ? undefined : 'hidden',
    position: 'relative',
  },
  ({ fitWidth, fitToWindow, theme }) => ({
    backgroundColor: theme.background,
    borderRadius: theme.borderRadius,
    maxHeight: fitToWindow ? '100%' : 'none',
    width: fitToWindow ? fitWidth : 'auto',
  }),
)

const ModalCloseButton = styled.button<{ isLightStyle: boolean }>(
  {
    padding: 10,
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: 10,
  },
  ({ isLightStyle, theme }) => ({
    '& svg': {
      '.light &': {
        fill: theme.text,
      },
      background: isLightStyle ? theme.background : 'transparent',
      border: `4px solid ${isLightStyle ? theme.background : 'transparent'}`,
      borderRadius: 15,
      fill: theme.textLight,
    },
  }),
)

export default Modal
